import { cn } from "@gettactic/helpers";
import React from "react";

export interface SegOption<T extends string> {
  value: T;
  label: React.ReactNode;
  /** Accessible name when the label is icon-only. */
  ariaLabel?: string;
}

export interface SegProps<T extends string> {
  value: T;
  onValueChange: (value: T) => void;
  options: SegOption<T>[];
  /** Accessible name for the group. */
  label: string;
  className?: string;
}

/**
 * Segmented control for small enumerations (on/off, alignment, cap style).
 * Replaces the hand-rolled toggle-group and its MutationObserver hack.
 */
export const Seg = <T extends string>({
  value,
  onValueChange,
  options,
  label,
  className
}: SegProps<T>) => {
  return (
    <div
      role="group"
      aria-label={label}
      className={cn("flex gap-0.5 rounded-md bg-neutral-100 p-0.5", className)}
    >
      {options.map((option) => {
        const isActive = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={isActive}
            aria-label={option.ariaLabel}
            onClick={() => onValueChange(option.value)}
            className={cn(
              "flex h-6 flex-1 items-center justify-center rounded-[5px] px-2 text-xs transition-colors",
              isActive
                ? "bg-background-primary text-neutral-900 shadow-sm"
                : "text-neutral-500 hover:text-neutral-700"
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
};
